import React, { useEffect, useRef } from 'react';
import { getAccountToday } from 'utils';
import {
  formatCoverage,
  formatShortDay,
  formatCount,
  surveyDaysOpen,
  pluralize
} from '../../fields.utils';
import {
  Section,
  Head,
  Hint,
  Track,
  Card,
  Title,
  Tag,
  Detail,
  Percent,
  Sub,
  Exported
} from './SurveyStrip.styles';

const cardDetail = (survey, today) => {
  const days = surveyDaysOpen(survey, today);
  const passes = `${survey.pass_count} ${pluralize(survey.pass_count, 'pass', 'passes')}`;
  return survey.is_open
    ? `since ${formatShortDay(survey.opened_on)} · ${days} days · ${passes}`
    : `${days} days · ${passes}`;
};

const cardCoverage = (survey, precision) => {
  if (survey.pass_count === 0) return { value: null, text: 'nothing imaged yet' };
  if (survey.coverage_ratio == null) return { value: null, text: '' };
  return { value: formatCoverage(survey.coverage_ratio, precision), text: 'imaged' };
};

export const SurveyStrip = ({
  surveys,
  selectedId,
  onSelect,
  precision,
  today,
  timezone,
  children
}) => {
  const trackRef = useRef(null);

  useEffect(() => {
    const track = trackRef.current;
    const selected = track?.querySelector('[aria-pressed="true"]');
    if (!selected) return;
    const left = selected.offsetLeft;
    const right = left + selected.offsetWidth;
    if (left < track.scrollLeft) track.scrollLeft = left - 8;
    else if (right > track.scrollLeft + track.clientWidth) {
      track.scrollLeft = right - track.clientWidth + 8;
    }
  }, [selectedId, surveys]);

  return (
    <Section data-testid='surveys-area'>
      <Head>
        <h3>Surveys</h3>
        <Hint>
          One survey is open and takes every pass that arrives. Finalising it freezes the
          map as a record and opens the next one.
        </Hint>
      </Head>
      <Track ref={trackRef}>
        {surveys.map(survey => {
          const coverage = cardCoverage(survey, precision);
          return (
            <Card
              key={survey.id}
              type='button'
              $open={survey.is_open}
              aria-pressed={survey.id === selectedId}
              onClick={() => onSelect(survey.id)}
            >
              <Title>
                {survey.is_open
                  ? 'Current survey'
                  : `${formatShortDay(survey.opened_on)} – ${formatShortDay(
                      survey.closed_on
                    )}`}
                <Tag $open={survey.is_open}>{survey.is_open ? 'open' : 'finalised'}</Tag>
              </Title>
              <Detail>{cardDetail(survey, today)}</Detail>
              <Percent $muted={coverage.value == null}>{coverage.value ?? '—'}</Percent>
              {survey.pass_count === 0 ? (
                <Sub>{coverage.text}</Sub>
              ) : (
                <Sub>
                  {coverage.value ? 'imaged · ' : ''}
                  <b>{formatCount(survey.stone_count)}</b> stones
                  {survey.is_open ? '' : ' on record'}
                </Sub>
              )}
              {survey.export ? (
                <Exported>
                  <b>{formatCount(survey.export.stones_selected)}</b> exported for picking
                  on{' '}
                  {formatShortDay(
                    getAccountToday(timezone, new Date(survey.export.created_at))
                  )}
                  <br />
                  {survey.export.filename}
                </Exported>
              ) : (
                !survey.is_open && (
                  <Exported $nil>No file was generated from this survey</Exported>
                )
              )}
            </Card>
          );
        })}
      </Track>
      {children}
    </Section>
  );
};
